import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkspaceContext = createContext(null);

// Define which routes belong to which workspace
const RECRUITING_ROUTES = [
  '/ats/clients',
  '/ats/requirements',
  '/ats/manual-selections',
  '/ats/proposal-mailer',
  '/ats/verifications',
  '/ats/dossier-templates',
];

const PORTAL_ROUTES = [
  '/ats/jobs',
  '/ats/candidates',
  '/ats/matches',
  '/ats/matching-resumes',
  '/ats/ai-job-search',
  '/ats/hr-pipeline',
  '/ats/hired-candidates',
  '/ats/emails',
];

const OPS_ROUTES = [
  '/ats/companies',
  '/ats/deal-closer',
  '/ats/money-alerts',
  '/ats/mailer',
  '/ats/retargeting',
  '/ats/hr-leads',
  '/ats/master-database',
  '/ats/insights',
  '/ats/activity',
  '/ats/productivity',
];

const PINNED_ROUTES = [
  '/ats',
  '/ats/my-desk',
  '/ats/todos',
  '/ats/calendar',
  '/ats/help',
  '/ats/settings',
];

// Legacy mode mapping (old "agency" workspace is now "ops")
const normalizeMode = (mode) => {
  if (mode === 'agency') return 'ops';
  if (['recruiting', 'portal', 'ops'].includes(mode)) return mode;
  return 'recruiting';
};

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [workspaceMode, setWorkspaceModeState] = useState(() => {
    const saved = localStorage.getItem('workspace_mode');
    return normalizeMode(saved);
  });
  const [loading, setLoading] = useState(true);

  // Check if user has ops (formerly agency) permission
  const hasAgencyPermission = useCallback(() => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    if (user.permissions?.agency_workspace) return true;
    return false;
  }, [user]);

  // Load user's saved workspace mode from server
  useEffect(() => {
    const loadWorkspaceMode = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/users/me/workspace-mode`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          const mode = normalizeMode(data.workspace_mode);
          setWorkspaceModeState(mode);
          localStorage.setItem('workspace_mode', mode);
        }
      } catch (e) {
        console.error('Failed to load workspace mode:', e);
      }
      setLoading(false);
    };

    loadWorkspaceMode();
  }, [user]);

  // Auto-switch mode based on current route (deep links keep the right menu open)
  useEffect(() => {
    if (loading || !user) return;

    const path = location.pathname;

    const isPinned = PINNED_ROUTES.some(r => path === r || path === r + '/');
    if (isPinned) return;

    let routeMode = null;
    if (RECRUITING_ROUTES.some(r => path.startsWith(r))) routeMode = 'recruiting';
    else if (PORTAL_ROUTES.some(r => path.startsWith(r))) routeMode = 'portal';
    else if (OPS_ROUTES.some(r => path.startsWith(r))) routeMode = 'ops';

    if (!routeMode || routeMode === workspaceMode) return;
    if (routeMode === 'ops' && !hasAgencyPermission()) return;

    setWorkspaceModeState(routeMode);
  }, [location.pathname, workspaceMode, hasAgencyPermission, loading, user]);

  // Save workspace mode to server
  const setWorkspaceMode = useCallback(async (mode) => {
    const normalized = normalizeMode(mode);
    setWorkspaceModeState(normalized);
    localStorage.setItem('workspace_mode', normalized);

    try {
      const token = localStorage.getItem('ats_token');
      await fetch(`${API_URL}/api/users/me/workspace-mode`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ workspace_mode: normalized })
      });
    } catch (e) {
      console.error('Failed to save workspace mode:', e);
    }
  }, []);

  // Determine current route's mode for UI indicator
  const getCurrentRouteMode = useCallback(() => {
    const path = location.pathname;
    if (RECRUITING_ROUTES.some(r => path.startsWith(r))) return 'recruiting';
    if (PORTAL_ROUTES.some(r => path.startsWith(r))) return 'portal';
    if (OPS_ROUTES.some(r => path.startsWith(r))) return 'ops';
    return null;
  }, [location.pathname]);

  const value = {
    workspaceMode,
    setWorkspaceMode,
    hasAgencyPermission: hasAgencyPermission(),
    loading,
    getCurrentRouteMode,
    RECRUITING_ROUTES,
    PORTAL_ROUTES,
    OPS_ROUTES,
    PINNED_ROUTES,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
